/**
 * 商品機能管理モジュール
 * 商品ホバー時の画像切り替えとズームアニメーションを制御
 */

import { gsap } from 'gsap';

/**
 * 商品リンクの hover でメイン画像を data 属性から切り替え、ズームアニメーションを適用します。
 */
export function setupProductHover() {
  const mainImage = document.getElementById('mainProductImage');
  if (!mainImage) return;

  // #product 内の .product_list にある全リンクを取得
  const productLinks = document.querySelectorAll('#product .product_list li a');
  // メイン画像の元の画像パスを保存
  const originalSrc = mainImage.src;

  // ズームアニメーション用のGSAPタイムライン
  let zoomTimeline = gsap.timeline({ paused: true });

  // キャッシュ済み画像を追跡するオブジェクト
  const imageCache = {};
  // 現在リクエスト中の画像のパス
  let currentRequestedImage = null;

  // 初期状態を設定
  gsap.set(mainImage, { scale: 1 });
  
  // 現在のアニメーション状態
  let isAnimating = false;
  
  // スケールをリセットする関数
  function resetScale() {
    // アニメーションを完全に停止
    zoomTimeline.kill();
    // スケールを1に強制リセット
    gsap.set(mainImage, { scale: 1 });
    isAnimating = false;
  }
  
  // ズームアニメーションを実行する関数
  function playZoomAnimation() {
    // 現在進行中のアニメーションをクリア
    zoomTimeline.clear();
    zoomTimeline.to(mainImage, {
      scale: 1.1,
      duration: 2,
      ease: 'power2.out',
      onStart: function() {
        isAnimating = true;
      },
      onComplete: function() {
        isAnimating = true; // アニメーション完了後も拡大状態を維持
      }
    });
    zoomTimeline.play();
  }

  // 画像の読み込みが完了しているか確認する関数
  function isImageCached(src) {
    // 既にキャッシュに登録済みの場合
    if (imageCache[src] === true) return true;
    
    // img要素を作成してキャッシュ状態を確認
    const img = new Image();
    // complete プロパティが true なら既にキャッシュされている
    img.src = src;
    const isCached = img.complete;
    
    // キャッシュに登録
    if (isCached) {
      imageCache[src] = true;
    }
    
    return isCached;
  }

  productLinks.forEach(function(link) {
    // data-hover-image 属性から画像パスを取得
    const hoverSrc = link.getAttribute('data-hover-image');
    if (!hoverSrc) return;

    link.addEventListener('mouseenter', function() {
      // 現在リクエスト中の画像をクリア
      currentRequestedImage = hoverSrc;
      
      // 現在のメイン画像のソースを保存
      const currentSrc = mainImage.src;
      
      // アニメーションを直ちに中止して初期状態にリセット
      resetScale();
      
      // 画像が既に読み込まれているか確認
      if (hoverSrc === currentSrc) {
        // すでに同じ画像が表示されている場合は直接アニメーションを実行
        playZoomAnimation();
      } else {
        // 画像を即座に切り替え
        mainImage.src = hoverSrc;
        
        // 画像がキャッシュされているか確認
        if (isImageCached(hoverSrc)) {
          // キャッシュされている場合は直ちにアニメーション実行
          console.log('画像がキャッシュされています');
          // 少し遅延を入れて確実にDOMが更新されてから実行
          setTimeout(playZoomAnimation, 10);
        } else {
          // キャッシュされていない場合は読み込み完了を待つ
          mainImage.onload = function() {
            // 現在リクエストされている画像と一致する場合のみアニメーション実行
            if (currentRequestedImage === hoverSrc) {
              console.log('画像が読み込まれました');
              playZoomAnimation();
            }
            // キャッシュに登録
            imageCache[hoverSrc] = true;
          };
        }
      }
    });

    link.addEventListener('mouseleave', function() {
      // 現在リクエスト中の画像をクリア
      currentRequestedImage = null;
      
      // アニメーションを直ちに中止して初期状態にリセット
      resetScale();
      
      // 元の画像に戻す
      if (mainImage.src !== originalSrc) {
        mainImage.src = originalSrc;
      }
    });
  });
  
  // 画像のプリロード
  function preloadImages() {
    // プリロード進捗状況の表示用メッセージ
    console.log('商品画像のプリロードを開始します');
    
    const imagesToLoad = [];
    productLinks.forEach(function(link) {
      const hoverSrc = link.getAttribute('data-hover-image');
      if (hoverSrc && !imageCache[hoverSrc]) {
        imagesToLoad.push(hoverSrc);
      }
    });
    
    // 元の画像もキャッシュに追加
    imageCache[originalSrc] = true;
    
    // すべての画像を読み込む
    let loadedCount = 0;
    imagesToLoad.forEach(function(src) {
      const img = new Image();
      img.onload = function() {
        loadedCount++;
        imageCache[src] = true;
        if (loadedCount === imagesToLoad.length) {
          console.log('すべての商品画像のプリロードが完了しました');
        }
      };
      img.onerror = function() {
        loadedCount++;
        console.error('画像の読み込みに失敗しました: ' + src);
        if (loadedCount === imagesToLoad.length) {
          console.log('商品画像のプリロードが完了しました（一部エラーあり）');
        }
      };
      img.src = src;
    });
  }

  // プリロードを実行
  preloadImages();
} 
